<template>
  <div class="erp-extract-details">
    <div v-if="hasData" class="erp-layout">
      <div v-if="periodLabel" class="erp-period">
        <span class="erp-period-label">Invoice Period</span>
        <strong class="erp-period-value">{{ periodLabel }}</strong>
      </div>

      <section
        v-for="section in summarySections"
        :key="section.key"
        class="erp-summary-section"
      >
        <div class="erp-hero-body">
          <div class="erp-hero-copy">
            <h2 class="erp-title">{{ section.title }}</h2>
            <p class="erp-subtitle">
              {{ formatCount(section.summary.total_count) }} {{ section.entityLabel }}
              across {{ formatCount(section.summary[section.counterpartyKey]) }} {{ section.counterpartyLabel }}
            </p>
          </div>
          <div class="erp-hero-total">
            <span class="erp-hero-label">Total Amount</span>
            <strong class="erp-hero-value">{{ formatCurrency(section.summary.total_amount) }}</strong>
          </div>
        </div>

        <div class="erp-split">
          <div class="erp-status-block paid">
            <span class="erp-status-label">{{ section.paidLabel }}</span>
            <div class="erp-status-row">
              <div class="erp-status-count-group">
                <div class="erp-status-primary">
                  <strong class="erp-status-count-value">{{ formatCount(section.paid.count) }}</strong>
                  <span class="erp-status-count-unit">{{ section.entityLabel }}</span>
                </div>
                <span class="erp-status-count-share">{{ section.paidCountShare }}% of all {{ section.entityLabel }}</span>
              </div>
              <div class="erp-status-amount-group">
                <strong class="erp-status-amount-value">{{ formatCurrency(section.paid.amount) }}</strong>
                <span class="erp-status-amount-label">amount</span>
              </div>
            </div>
          </div>

          <div class="erp-status-block outstanding">
            <span class="erp-status-label">{{ section.outstandingLabel }}</span>
            <div class="erp-status-row">
              <div class="erp-status-count-group">
                <div class="erp-status-primary">
                  <strong class="erp-status-count-value">{{ formatCount(section.outstanding.count) }}</strong>
                  <span class="erp-status-count-unit">{{ section.entityLabel }}</span>
                </div>
                <span class="erp-status-count-share">{{ section.outstandingCountShare }}% of all {{ section.entityLabel }}</span>
              </div>
              <div class="erp-status-amount-group">
                <strong class="erp-status-amount-value">{{ formatCurrency(section.outstanding.amount) }}</strong>
                <span class="erp-status-amount-label">amount</span>
              </div>
            </div>
          </div>
        </div>

        <div class="erp-metrics">
          <div class="erp-metric">
            <span class="erp-metric-label">Total {{ section.entityTitle }}</span>
            <strong class="erp-metric-value">{{ formatCount(section.summary.total_count) }}</strong>
          </div>
          <div class="erp-metric">
            <span class="erp-metric-label">Unique {{ section.counterpartyTitle }}</span>
            <strong class="erp-metric-value">{{ formatCount(section.summary[section.counterpartyKey]) }}</strong>
          </div>
          <div class="erp-metric">
            <span class="erp-metric-label">Paid Rate</span>
            <strong class="erp-metric-value">{{ section.paidCountShare }}%</strong>
          </div>
        </div>
      </section>
    </div>

    <div v-else class="erp-empty">
      <i class="las la-file-invoice-dollar"></i>
      <h3>No ERP Extract Data</h3>
      <p>No summary is available for this file.</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ErpExtractDetails',
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  computed: {
    periodLabel() {
      const period = this.data?.invoices_summary?.period;
      if (!period?.first_issue_date || !period?.last_issue_date) {
        return '';
      }

      return this.formatPeriod(period.first_issue_date, period.last_issue_date);
    },
    hasData() {
      return !!(this.data?.invoices_summary || this.data?.bills_summary);
    },
    summarySections() {
      const configs = [
        {
          key: 'invoices',
          dataKey: 'invoices_summary',
          title: 'Invoices Summary',
          entityLabel: 'invoices',
          entityTitle: 'Invoices',
          counterpartyLabel: 'customers',
          counterpartyTitle: 'Customers',
          counterpartyKey: 'unique_customers',
          paidLabel: 'Paid Invoices',
          outstandingLabel: 'Outstanding Invoices'
        },
        {
          key: 'bills',
          dataKey: 'bills_summary',
          title: 'Bills Summary',
          entityLabel: 'bills',
          entityTitle: 'Bills',
          counterpartyLabel: 'vendors',
          counterpartyTitle: 'Vendors',
          counterpartyKey: 'unique_vendors',
          paidLabel: 'Paid Bills',
          outstandingLabel: 'Outstanding Bills'
        }
      ];

      return configs
        .map((config) => {
          const summary = this.data?.[config.dataKey];
          if (!summary) return null;

          const paid = summary.paid || {};
          const outstanding = summary.outstanding || {};
          const totalCount = Number(summary.total_count) || 0;
          const paidCount = Number(paid.count) || 0;
          const outstandingCount = Number(outstanding.count) || 0;

          return {
            ...config,
            summary,
            paid,
            outstanding,
            paidCountShare: totalCount ? Math.round((paidCount / totalCount) * 100) : 0,
            outstandingCountShare: totalCount ? Math.round((outstandingCount / totalCount) * 100) : 0
          };
        })
        .filter(Boolean);
    }
  },
  methods: {
    formatCount(value) {
      if (value === null || value === undefined || value === '') return 'N/A';
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return String(value);
      return numericValue.toLocaleString('en-US');
    },
    formatCurrency(value) {
      if (value === null || value === undefined || value === '') return 'N/A';
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return String(value);

      return new Intl.NumberFormat('en-AE', {
        style: 'currency',
        currency: 'AED',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numericValue);
    },
    formatDate(value) {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);

      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },
    formatPeriod(start, end) {
      return `${this.formatDate(start)} – ${this.formatDate(end)}`;
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../index.scss';

.erp-extract-details {
  width: 100%;
}

.erp-layout {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.erp-period {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;
}

.erp-period-label {
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-family: $brand-font-medium;
}

.erp-period-value {
  color: #0f172a;
  font-size: 1.5rem;
  font-weight: 700;
  font-family: $brand-font-medium;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
}

.erp-summary-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding-top: 0.25rem;

  & + .erp-summary-section {
    padding-top: 2rem;
    border-top: 1px solid #e2e8f0;
  }
}

.erp-hero-body {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.5rem;
}

.erp-title {
  margin: 0 0 0.35rem;
  color: #0f172a;
  font-size: 1.5rem;
  font-weight: 600;
  font-family: $brand-font-medium;
  line-height: 1.2;
}

.erp-subtitle {
  margin: 0;
  color: #64748b;
  font-size: 0.95rem;
  font-family: $brand-font-regular;
}

.erp-hero-total {
  text-align: right;
  flex-shrink: 0;
}

.erp-hero-label {
  display: block;
  margin-bottom: 0.25rem;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  font-family: $brand-font-medium;
}

.erp-hero-value {
  color: #0f172a;
  font-size: 1.75rem;
  font-weight: 700;
  font-family: $brand-font-medium;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
}

.erp-split {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem;
}

.erp-status-block {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  background: transparent;

  &.paid {
    .erp-status-count-value,
    .erp-status-amount-value {
      color: #22C55E;
    }

    padding-right: 1.25rem;
  }

  &.outstanding {
    .erp-status-count-value,
    .erp-status-amount-value {
      color: #F59E0B;
    }

    padding-left: 1.25rem;
  }
}

.erp-status-label {
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  font-family: $brand-font-medium;
}

.erp-status-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.25rem;
  margin-top: 0.35rem;
}

.erp-status-count-group,
.erp-status-amount-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.erp-status-amount-group {
  text-align: right;
  flex-shrink: 0;
}

.erp-status-primary {
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
}

.erp-status-count-value,
.erp-status-amount-value {
  font-size: 1.75rem;
  font-weight: 700;
  font-family: $brand-font-medium;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
}

.erp-status-count-unit,
.erp-status-amount-label {
  color: #64748b;
  font-size: 0.9rem;
  font-weight: 500;
  font-family: $brand-font-regular;
}

.erp-status-count-share {
  color: #475569;
  font-size: 0.82rem;
  font-weight: 500;
  font-family: $brand-font-medium;
}

.erp-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.erp-metric {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: 0.85rem 0;
  border-top: 1px solid #e2e8f0;
}

.erp-metric-label {
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  font-family: $brand-font-medium;
}

.erp-metric-value {
  color: #0f172a;
  font-size: 1.15rem;
  font-weight: 700;
  font-family: $brand-font-medium;
  font-variant-numeric: tabular-nums;
}

.erp-empty {
  text-align: center;
  padding: 3rem 1.5rem;
  color: #64748b;

  i {
    font-size: 2.5rem;
    margin-bottom: 0.75rem;
    display: block;
  }

  h3 {
    margin: 0 0 0.35rem;
    color: #1e293b;
    font-size: 1.15rem;
    font-family: $brand-font-medium;
  }

  p {
    margin: 0;
    font-size: 0.95rem;
    font-family: $brand-font-regular;
  }
}

@media (max-width: 768px) {
  .erp-hero-body {
    flex-direction: column;
    align-items: flex-start;
  }

  .erp-hero-total {
    text-align: left;
  }

  .erp-split,
  .erp-metrics {
    grid-template-columns: 1fr;
  }

  .erp-status-block {
    &.paid,
    &.outstanding {
      padding-left: 0;
      padding-right: 0;
    }
  }
}
</style>
